/**
 * How much cheaper a cached input token is than a fresh one: what the gateway page's Cache
 * column shows.
 *
 * It used to show the cached price as a share of the fresh one, so DeepSeek's 2% was the best
 * figure on the page and read as the worst: a cache figure reads as a hit rate, where higher is
 * better. A saving reads one way only, and the "Min cache %" filter, which kept the models whose
 * cache saved least, now keeps those that save most.
 *
 * @module apps/web/pages/cache-saving
 */

/** great: 75% off or more. fair: 50% or more. poor: less, where caching barely pays. */
export type CacheTier = 'great' | 'fair' | 'poor';

export interface CacheSaving {
  /** Whole percent off the fresh input price, 0 to 100. */
  percent: number;
  tier: CacheTier;
}

/** The saving, or null where the model caches but its cached price is not known. */
export function cacheSavingOf(model: { upstreamInputCostPerMillion: number; upstreamCachedInputCostPerMillion: number }): CacheSaving | null {
  const fresh = model.upstreamInputCostPerMillion;
  const cached = model.upstreamCachedInputCostPerMillion;
  if (!(fresh > 0) || !(cached > 0)) return null;
  const percent = Math.max(0, Math.min(100, Math.round((1 - cached / fresh) * 100)));
  return { percent, tier: percent >= 75 ? 'great' : percent >= 50 ? 'fair' : 'poor' };
}
